import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { FeatureService, ModalService } from 'services';
import { Backdrop, ModalOptions, Position, Size } from 'components/modal';
import { BehaviorSubject, Observable } from 'rxjs';
import { TooltipPosition, TooltipPositionX, TooltipPositionY } from 'fc-ui-components';

@Component({
    selector: 'fc-request-access-modal',
    templateUrl: './request-access-modal.component.html',
    styleUrls: ['./request-access-modal.component.less'],
    standalone: false
})
export class RequestAccessModalComponent implements OnChanges {
    @Input() modalId: string;
    @Input() options: ModalOptions = { size: Size.Md, backdrop: Backdrop.Static, position: Position.Center };
    @Input() requestedProductTerms: {
        pdfLink: string;
        pdfLinkTranslationKey: string;
    }[];
    @Input() enableRetailerTerms = true;
    @Input() brandId: string;

    @Output() reject = new EventEmitter<void>();
    @Output() accept = new EventEmitter<any>();

    public retailerTermsFile: BehaviorSubject<FileModel> = new BehaviorSubject(undefined);
    public message = '';
    public vat = '';
    public brandTermsAccepted = false;
    public tooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Top
    };
    public retailerTermsTooltipOpen = false;
    public showVat: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private modalService: ModalService,
        private featureService: FeatureService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.brandId)
            this.featureService
                .isFeatureEnabled('vat-in-connection-modal', { brandId: this.brandId })
                .then((enabled) => {
                    this.showVat.next(enabled);
                });
    }

    public updateMessage(message: string) {
        this.message = message;
    }

    public requestAccess() {
        this.accept.emit({
            message: this.vat ? `${this.message}\n VAT Number = ${this.vat}` : this.message,
            retailerTermsFile: this.retailerTermsFile.value
        });
    }

    public selectFile(event) {
        const file = event.target.files[0];
        this.mapFileToObservable(file).subscribe((fileResult) => {
            const fileExtensionPosition = file.name.lastIndexOf('.');
            this.retailerTermsFile.next({
                content: fileResult.result,
                extension: file.name.substring(fileExtensionPosition + 1),
                filename: file.name.substring(0, fileExtensionPosition),
                size: file.size
            });
        });
    }

    mapFileToObservable(file: File, reader: FileReader = new FileReader()) {
        return new Observable<{ result: FileReader['result'] }>((subscriber) => {
            if (!(file instanceof File)) {
                subscriber.error(new Error('`file` must be an instance of File.'));
                return;
            }

            reader.onerror = (err) => subscriber.error(err);
            reader.onabort = (err) => subscriber.error(err);
            reader.onload = (event) => {
                if (event.target.readyState === reader.DONE) {
                    return subscriber.next({ result: reader.result });
                }
            };
            reader.onloadend = () => subscriber.complete();

            reader.readAsDataURL(file);
        });
    }

    public deselect() {
        this.retailerTermsFile.next(undefined);
    }

    public setBrandTermsAccepted(checked: boolean) {
        this.brandTermsAccepted = checked;
    }

    public updateVat(vat: string) {
        this.vat = vat;
    }

    public close() {
        this.message = '';
        this.brandTermsAccepted = false;
        this.retailerTermsFile.next(undefined);
        this.modalService.close(this.modalId);
    }
}

export class FileModel {
    filename: string;
    extension: string;
    size: number;
    content: string | ArrayBuffer;
}
